import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import pluralize from 'pluralize';

import { calculateItemTotal } from 'utils/common';
import {
  evaluateFormulaDisplayValue,
  expressionFieldFor,
  isFormulaExpressionValue,
  isIncompleteFormulaExpressionValue,
  isIncompleteDecimalValue,
  normalizeFormulaPayloadValue,
} from '../utils/rowPayload';

const EXCLUDED_MULTI_EDIT_COLUMNS = new Set([
  'checkbox',
  'item_total',
  'fringes',
  'fringe_total',
  'column_total',
]);

const ARROW_NAVIGATION_KEYS = new Set([
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
  'ArrowUp',
]);

const getStableRowKey = row => {
  const rowKey = row?.id ?? row?.row_id;
  return rowKey == null ? '' : String(rowKey);
};

const getCellKey = (rowKey, columnKey) => `${columnKey}:${rowKey}`;

const isServerBackedRow = row => {
  const numericId = Number(row?.id);
  return Number.isInteger(numericId) && numericId > 0;
};

export const isMultiCellEditableRow = row =>
  isServerBackedRow(row) && !row?.is_placeholder && !row?._isDraft;

const isSelectableColumn = (column, hiddenColumn = {}) =>
  Boolean(column?.key) &&
  !hiddenColumn[column.key] &&
  !EXCLUDED_MULTI_EDIT_COLUMNS.has(column.key) &&
  (column.type === 'text' || column.type === 'select');

const isTextOperationColumn = column =>
  (column?.type === 'text' || column?.type === 'select') &&
  column?.dataType !== 'decimal';

const isFormulaEditableColumn = column =>
  column?.dataType === 'decimal' &&
  column?.type !== 'showValue' &&
  ['quantity', 'rate', 'multiplier'].includes(column.key);

const normalizeRequiredDecimalDefault = (value, fallback = '1') => {
  if (isFormulaExpressionValue(value)) return fallback;
  const parsedValue = Number.parseFloat(value);
  return Number.isFinite(parsedValue) && parsedValue >= 0 ? value : fallback;
};

const normalizeNumericDefaults = row => ({
  ...row,
  quantity: normalizeRequiredDecimalDefault(row.quantity),
  multiplier: normalizeRequiredDecimalDefault(row.multiplier),
});

const applyPluralizedUnit = (row, column) => {
  if (!column?.pluralize || !row[column.pluralize.key]) return row;

  if (column.key === 'quantity') {
    return {
      ...row,
      unit:
        row.quantity <= 1
          ? row.unit?.replace(/s$/, '')
          : pluralize(row.unit, row.quantity),
    };
  }

  if (column.key === 'multiplier') {
    return {
      ...row,
      multiplier_unit:
        row.multiplier <= 1
          ? row.multiplier_unit?.replace(/s$/, '')
          : pluralize(row.multiplier_unit, row.multiplier),
    };
  }

  return row;
};

const getTextChangeOperation = ({
  previousValue,
  value,
  selectionStart,
  selectionEnd,
}) => {
  const previousText = String(previousValue ?? '');
  const nextText = String(value ?? '');

  if (
    previousText === nextText ||
    !Number.isInteger(selectionStart) ||
    !Number.isInteger(selectionEnd)
  ) {
    return null;
  }

  let prefixLength = 0;
  while (
    prefixLength < previousText.length &&
    prefixLength < nextText.length &&
    previousText[prefixLength] === nextText[prefixLength]
  ) {
    prefixLength++;
  }

  let previousSuffixIndex = previousText.length;
  let nextSuffixIndex = nextText.length;
  while (
    previousSuffixIndex > prefixLength &&
    nextSuffixIndex > prefixLength &&
    previousText[previousSuffixIndex - 1] === nextText[nextSuffixIndex - 1]
  ) {
    previousSuffixIndex--;
    nextSuffixIndex--;
  }

  const isEndRelativeEdit =
    previousSuffixIndex === previousText.length &&
    selectionStart === nextText.length &&
    selectionEnd === nextText.length;

  return {
    start: prefixLength,
    end: previousSuffixIndex,
    insertedText: nextText.slice(prefixLength, nextSuffixIndex),
    isEndRelativeEdit,
    startFromEnd: previousText.length - prefixLength,
    endFromEnd: previousText.length - previousSuffixIndex,
  };
};

const applyTextChangeOperation = (targetValue, operation) => {
  const targetText = String(targetValue ?? '');
  const start = operation.isEndRelativeEdit
    ? Math.max(0, targetText.length - operation.startFromEnd)
    : Math.min(operation.start, targetText.length);
  const end = operation.isEndRelativeEdit
    ? Math.max(start, targetText.length - operation.endFromEnd)
    : Math.min(Math.max(start, operation.end), targetText.length);

  return `${targetText.slice(0, start)}${
    operation.insertedText
  }${targetText.slice(end)}`;
};

export const useMultiCellSelection = ({
  rows,
  columns,
  hiddenColumn,
  detailSheetType,
  canEdit,
  addRow,
  budgetGlobals = [],
  overtimeConfig,
  onClipboardSelectionChange,
}) => {
  const [selection, setSelection] = useState({
    columnKey: null,
    rowKeys: [],
    anchorRowKey: null,
    focusRowKey: null,
    isDragging: false,
  });
  const deferredClearTimeoutRef = useRef(null);
  const pendingTextValuesRef = useRef(new Map());

  const selectableRows = useMemo(
    () => rows.filter(isMultiCellEditableRow),
    [rows]
  );

  const rowByKey = useMemo(() => {
    return new Map(rows.map(row => [getStableRowKey(row), row]));
  }, [rows]);

  const selectableRowIndexByKey = useMemo(() => {
    return new Map(
      selectableRows.map((row, index) => [getStableRowKey(row), index])
    );
  }, [selectableRows]);

  const selectedCellKeys = useMemo(() => {
    if (!selection.columnKey) return new Set();
    return new Set(
      selection.rowKeys.map(rowKey => getCellKey(rowKey, selection.columnKey))
    );
  }, [selection.columnKey, selection.rowKeys]);

  const selectableColumnKeys = useMemo(
    () =>
      new Set(
        columns
          .filter(column => isSelectableColumn(column, hiddenColumn))
          .map(column => column.key)
      ),
    [columns, hiddenColumn]
  );

  const getRangeRowKeys = useCallback(
    (startRowKey, endRowKey) => {
      const startIndex = selectableRowIndexByKey.get(startRowKey);
      const endIndex = selectableRowIndexByKey.get(endRowKey);

      if (startIndex == null || endIndex == null) return [endRowKey];

      const minIndex = Math.min(startIndex, endIndex);
      const maxIndex = Math.max(startIndex, endIndex);
      return selectableRows
        .slice(minIndex, maxIndex + 1)
        .map(row => getStableRowKey(row));
    },
    [selectableRows, selectableRowIndexByKey]
  );

  const getCellContextFromElement = useCallback(
    element => {
      const formulaBarElement = element?.closest?.(
        '[data-formula-bar-input-name]'
      );
      const activeCellName =
        formulaBarElement?.dataset?.formulaBarInputName ||
        element?.getAttribute?.('name');
      const [columnKey, rowIndexText] = activeCellName?.split?.(',') || [];
      const rowIndex = Number.parseInt(rowIndexText, 10);

      if (!columnKey || !Number.isInteger(rowIndex)) return null;

      const row = rows[rowIndex];
      if (!isMultiCellEditableRow(row)) return null;

      return {
        columnKey,
        rowKey: getStableRowKey(row),
      };
    },
    [rows]
  );

  const getActiveCellContext = useCallback(() => {
    return getCellContextFromElement(document.activeElement);
  }, [getCellContextFromElement]);

  const focusedElementIsInSelection = useCallback(
    (element, activeSelection = selection) => {
      if (!activeSelection.columnKey || !activeSelection.rowKeys.length) {
        return true;
      }

      const activeCell = getCellContextFromElement(element);
      return (
        activeCell?.columnKey === activeSelection.columnKey &&
        activeSelection.rowKeys.includes(activeCell.rowKey)
      );
    },
    [getCellContextFromElement, selection]
  );

  const extendKeyboardSelection = useCallback(
    event => {
      if (
        !canEdit ||
        !event.shiftKey ||
        event.metaKey ||
        event.ctrlKey ||
        (event.key !== 'ArrowDown' && event.key !== 'ArrowUp')
      ) {
        return false;
      }

      const activeCell = getActiveCellContext();
      const columnKey = selection.columnKey || activeCell?.columnKey;
      if (!columnKey || !selectableColumnKeys.has(columnKey)) return false;

      const focusRowKey =
        selection.focusRowKey ||
        activeCell?.rowKey ||
        selection.rowKeys[selection.rowKeys.length - 1];
      const anchorRowKey =
        selection.anchorRowKey || activeCell?.rowKey || focusRowKey;
      const focusIndex = selectableRowIndexByKey.get(focusRowKey);

      if (!anchorRowKey || focusIndex == null) return false;

      const direction = event.key === 'ArrowDown' ? 1 : -1;
      const nextRow = selectableRows[focusIndex + direction];
      if (!nextRow) return true;

      const nextFocusRowKey = getStableRowKey(nextRow);
      const nextSelection = {
        columnKey,
        rowKeys: getRangeRowKeys(anchorRowKey, nextFocusRowKey),
        anchorRowKey,
        focusRowKey: nextFocusRowKey,
        isDragging: false,
      };

      onClipboardSelectionChange?.(nextSelection);
      setSelection(nextSelection);

      return true;
    },
    [
      canEdit,
      getActiveCellContext,
      getRangeRowKeys,
      selectableColumnKeys,
      selectableRowIndexByKey,
      selectableRows,
      onClipboardSelectionChange,
      selection.anchorRowKey,
      selection.columnKey,
      selection.focusRowKey,
      selection.rowKeys,
    ]
  );

  const clearSelection = useCallback(() => {
    if (deferredClearTimeoutRef.current) {
      clearTimeout(deferredClearTimeoutRef.current);
      deferredClearTimeoutRef.current = null;
    }

    pendingTextValuesRef.current.clear();
    setSelection({
      columnKey: null,
      rowKeys: [],
      anchorRowKey: null,
      focusRowKey: null,
      isDragging: false,
    });
  }, []);

  const handleCellMouseDown = useCallback(
    ({ event, rowData, column }) => {
      if (!canEdit || event.button !== 0) return;
      if (
        !isMultiCellEditableRow(rowData) ||
        !isSelectableColumn(column, hiddenColumn)
      ) {
        return;
      }

      const rowKey = getStableRowKey(rowData);
      const columnKey = column.key;
      const isToggleClick = event.metaKey || event.ctrlKey;

      setSelection(prev => {
        if (isToggleClick) {
          const isSameColumn = prev.columnKey === columnKey;
          const currentRowKeys = isSameColumn ? prev.rowKeys : [];
          const isAlreadySelected = currentRowKeys.includes(rowKey);
          const nextRowKeys = isAlreadySelected
            ? currentRowKeys.filter(selectedRowKey => selectedRowKey !== rowKey)
            : [...currentRowKeys, rowKey];

          const nextSelection = {
            columnKey: nextRowKeys.length ? columnKey : null,
            rowKeys: nextRowKeys,
            anchorRowKey: rowKey,
            focusRowKey: rowKey,
            isDragging: false,
          };

          onClipboardSelectionChange?.(nextSelection);
          return nextSelection;
        }

        const clickedExistingSelection =
          prev.columnKey === columnKey &&
          prev.rowKeys.length > 1 &&
          prev.rowKeys.includes(rowKey);

        if (clickedExistingSelection) {
          const nextSelection = {
            ...prev,
            anchorRowKey: rowKey,
            focusRowKey: rowKey,
            isDragging: true,
          };

          onClipboardSelectionChange?.(nextSelection);
          return nextSelection;
        }

        const nextSelection = {
          columnKey,
          rowKeys: [rowKey],
          anchorRowKey: rowKey,
          focusRowKey: rowKey,
          isDragging: true,
        };

        onClipboardSelectionChange?.(nextSelection);
        return nextSelection;
      });
    },
    [canEdit, hiddenColumn, onClipboardSelectionChange]
  );

  const handleCellMouseEnter = useCallback(
    ({ rowData, column }) => {
      if (!canEdit || !isMultiCellEditableRow(rowData)) return;
      const rowKey = getStableRowKey(rowData);
      const columnKey = column?.key;

      setSelection(prev => {
        if (
          !prev.isDragging ||
          prev.columnKey !== columnKey ||
          !prev.anchorRowKey
        ) {
          return prev;
        }

        const rowKeys = getRangeRowKeys(prev.anchorRowKey, rowKey);
        const nextSelection = {
          ...prev,
          rowKeys,
          focusRowKey: rowKey,
        };

        onClipboardSelectionChange?.(nextSelection);
        return nextSelection;
      });
    },
    [canEdit, getRangeRowKeys, onClipboardSelectionChange]
  );

  const isCellSelected = useCallback(
    (rowData, column) => {
      const rowKey = getStableRowKey(rowData);
      return selectedCellKeys.has(getCellKey(rowKey, column?.key));
    },
    [selectedCellKeys]
  );

  const isPrecisionTextEditActive = useCallback(
    (rowData, column) => {
      const rowKey = getStableRowKey(rowData);
      return (
        isTextOperationColumn(column) &&
        selection.columnKey === column?.key &&
        selection.rowKeys.length > 1 &&
        selectedCellKeys.has(getCellKey(rowKey, column?.key))
      );
    },
    [selectedCellKeys, selection.columnKey, selection.rowKeys.length]
  );

  const applyValueChange = useCallback(
    ({
      sourceRowKey,
      column,
      value,
      previousValue,
      selectionStart,
      selectionEnd,
    }) => {
      if (
        !canEdit ||
        !selection.columnKey ||
        selection.columnKey !== column?.key ||
        selection.rowKeys.length <= 1 ||
        !selectableColumnKeys.has(column.key)
      ) {
        return;
      }

      const isFormulaInput =
        isFormulaEditableColumn(column) && isFormulaExpressionValue(value);

      if (
        column?.dataType === 'decimal' &&
        ((!isFormulaInput && isIncompleteDecimalValue(value, column)) ||
          (isFormulaInput && isIncompleteFormulaExpressionValue(value)))
      ) {
        return;
      }

      const textOperation = isTextOperationColumn(column)
        ? getTextChangeOperation({
            previousValue,
            value,
            selectionStart,
            selectionEnd,
          })
        : null;
      const revision = Date.now();
      selection.rowKeys.forEach(rowKey => {
        if (rowKey === sourceRowKey) return;

        const targetRow = rowByKey.get(rowKey);
        if (!targetRow || !isMultiCellEditableRow(targetRow)) return;

        const cellKey = getCellKey(rowKey, column.key);
        const targetValue = textOperation
          ? pendingTextValuesRef.current.get(cellKey) ?? targetRow[column.key]
          : value;
        const nextValue = textOperation
          ? applyTextChangeOperation(targetValue, textOperation)
          : value;
        const formulaDisplayValue = isFormulaInput
          ? evaluateFormulaDisplayValue(nextValue, budgetGlobals)
          : null;
        if (isFormulaInput && formulaDisplayValue === null) {
          return;
        }

        if (textOperation) {
          pendingTextValuesRef.current.set(cellKey, nextValue);
        }

        let nextRow = {
          ...targetRow,
          [column.key]: isFormulaInput
            ? formulaDisplayValue ?? targetRow[column.key]
            : nextValue,
          ...(isFormulaEditableColumn(column)
            ? {
                [expressionFieldFor(column.key)]: isFormulaInput
                  ? normalizeFormulaPayloadValue(nextValue)
                  : '',
              }
            : {}),
        };
        if (!isFormulaInput) {
          nextRow = normalizeNumericDefaults(nextRow);
        }
        nextRow = applyPluralizedUnit(nextRow, column);
        nextRow.item_total = calculateItemTotal(nextRow, overtimeConfig);
        nextRow.column_total =
          parseFloat(nextRow.item_total || 0) +
          parseFloat(nextRow.fringe_total || 0);

        addRow({
          ...nextRow,
          _multiEditRevision: revision,
          _multiEditField: column.key,
        });
      });
    },
    [
      addRow,
      budgetGlobals,
      overtimeConfig,
      canEdit,
      rowByKey,
      selectableColumnKeys,
      selection.columnKey,
      selection.rowKeys,
    ]
  );

  const getPresenceCells = useCallback(
    (rowData, column) => {
      const rowKey = getStableRowKey(rowData);
      const isSelected = selectedCellKeys.has(getCellKey(rowKey, column?.key));
      const shouldPublishSelection =
        isSelected &&
        selection.columnKey === column?.key &&
        selection.rowKeys.length > 1;

      if (!shouldPublishSelection) return [];

      return selection.rowKeys
        .map(selectedRowKey => rowByKey.get(selectedRowKey))
        .filter(isMultiCellEditableRow)
        .map(row => ({
          detail_sheet_type: detailSheetType,
          row_id: row.id,
          column_key: column.key,
          row_label: row.item || '',
        }));
    },
    [
      detailSheetType,
      rowByKey,
      selectedCellKeys,
      selection.columnKey,
      selection.rowKeys,
    ]
  );

  const getPresenceCellsForCell = useCallback(
    cell => {
      if (!cell?.rowId || !cell?.columnKey) return [];

      const rowData = rowByKey.get(String(cell.rowId));
      const column = columns.find(col => col.key === cell.columnKey);
      if (!rowData || !column) return [];

      return getPresenceCells(rowData, column);
    },
    [columns, getPresenceCells, rowByKey]
  );

  useEffect(() => {
    const handleMouseUp = () => {
      setSelection(prev =>
        prev.isDragging ? { ...prev, isDragging: false } : prev
      );
    };
    window.addEventListener('mouseup', handleMouseUp);
    return () => window.removeEventListener('mouseup', handleMouseUp);
  }, []);

  useEffect(() => {
    const handleKeyDown = event => {
      if (extendKeyboardSelection(event)) {
        event.preventDefault();
        return;
      }

      const isPlainArrowNavigation =
        ARROW_NAVIGATION_KEYS.has(event.key) &&
        !event.shiftKey &&
        !event.metaKey &&
        !event.ctrlKey;

      if (event.key === 'Tab') {
        if (deferredClearTimeoutRef.current) {
          clearTimeout(deferredClearTimeoutRef.current);
        }
        deferredClearTimeoutRef.current = setTimeout(() => {
          deferredClearTimeoutRef.current = null;
          clearSelection();
        }, 0);
        return;
      }

      if (event.key === 'Escape' || isPlainArrowNavigation) {
        clearSelection();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      if (deferredClearTimeoutRef.current) {
        clearTimeout(deferredClearTimeoutRef.current);
        deferredClearTimeoutRef.current = null;
      }
    };
  }, [clearSelection, extendKeyboardSelection]);

  useEffect(() => {
    const handleFocusIn = event => {
      if (!selection.columnKey || !selection.rowKeys.length) return;
      if (focusedElementIsInSelection(event.target)) return;

      clearSelection();
    };

    window.addEventListener('focusin', handleFocusIn);
    return () => window.removeEventListener('focusin', handleFocusIn);
  }, [
    clearSelection,
    focusedElementIsInSelection,
    selection.columnKey,
    selection.rowKeys.length,
  ]);

  useEffect(() => {
    setSelection(prev => {
      if (!prev.rowKeys.length || !prev.columnKey) return prev;

      const rowKeys = prev.rowKeys.filter(rowKey => rowByKey.has(rowKey));
      const columnIsStillSelectable = selectableColumnKeys.has(prev.columnKey);

      if (!rowKeys.length || !columnIsStillSelectable) {
        pendingTextValuesRef.current.clear();
        return {
          columnKey: null,
          rowKeys: [],
          anchorRowKey: null,
          focusRowKey: null,
          isDragging: false,
        };
      }

      if (rowKeys.length === prev.rowKeys.length) return prev;

      pendingTextValuesRef.current.clear();
      return {
        ...prev,
        rowKeys,
        focusRowKey: rowKeys.includes(prev.focusRowKey)
          ? prev.focusRowKey
          : rowKeys[rowKeys.length - 1],
      };
    });
  }, [rowByKey, selectableColumnKeys]);

  return {
    selectedCellKeys,
    selection,
    isCellSelected,
    isPrecisionTextEditActive,
    handleCellMouseDown,
    handleCellMouseEnter,
    applyValueChange,
    getPresenceCells,
    getPresenceCellsForCell,
    clearSelection,
  };
};
